import React, { useState } from 'react';
import { format, subDays, startOfDay, endOfDay, isSameYear } from 'date-fns';
import { enUS, fr as frLocale, es as esLocale } from 'date-fns/locale';
import { Calendar, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import type { DateRangeType } from '../../hooks/useYouTubeAnalytics';

registerLocale('fr', frLocale);
registerLocale('es', esLocale);

interface DateRangePickerProps {
  dateRange: DateRangeType;
  customDateRange: { start: Date; end: Date };
  onDateRangeChange: (newRange: DateRangeType) => void;
  onCustomDateRangeChange: (range: { start: Date; end: Date }) => void;
  baseDate?: Date;
  fullWidth?: boolean;
}

// LinkedIn (and most social analytics APIs) cap historical data at a rolling 12 months.
// Keep custom ranges within that window so we never query past the provider's retention.
const MAX_HISTORY_DAYS = 365;

export const dateRangeOptions = [
  { key: 'thisMonth', value: 'this-month' },
  { key: 'lastMonth', value: 'last-month' },
  { key: 'last15days', value: 15 },
  { key: 'last30days', value: 30 },
  { key: 'last90days', value: 90 },
  { key: 'thisYear', value: 'this-year' },
  { key: 'last365days', value: 365 },
  { key: 'custom', value: 'custom' }
] as const;

export function DateRangePicker({
  dateRange,
  customDateRange,
  onDateRangeChange,
  onCustomDateRangeChange,
  baseDate = new Date(),
  fullWidth = false
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const lang = (i18n?.language || '').toLowerCase();
  const isFrench = lang.startsWith('fr');
  const isSpanish = lang.startsWith('es');
  const locale = isFrench ? frLocale : isSpanish ? esLocale : enUS;

  // Internal state for temporary custom date range
  const [tempCustomStart, setTempCustomStart] = useState<Date>(customDateRange.start);
  const [tempCustomEnd, setTempCustomEnd] = useState<Date>(customDateRange.end);

  // Effect to sync temp dates when the main customDateRange prop changes
  // or when the dropdown opens and current mode is 'custom'.
  React.useEffect(() => {
    if (dateRange === 'custom') {
      setTempCustomStart(customDateRange.start);
      setTempCustomEnd(customDateRange.end);
    }
    // If not 'custom', we might want to reset temp dates to props or keep them.
    // For now, they only explicitly sync when 'custom' is active.
  }, [customDateRange.start, customDateRange.end, dateRange, isOpen]);

  // Bounds for the custom date inputs: yesterday (inclusive) down to 365 days ago.
  const customMaxDate = subDays(new Date(), 1);
  const customMinDate = subDays(new Date(), MAX_HISTORY_DAYS);

  // Clamp any date to the allowed [min, max] window.
  const clampToWindow = (d: Date): Date => {
    if (d < customMinDate) return startOfDay(customMinDate);
    if (d > customMaxDate) return endOfDay(customMaxDate);
    return d;
  };

  // Compute the displayed date range label
  const displayedRange = (() => {
    if (dateRange === 'custom') {
      const fmt = isFrench ? 'd MMM, yyyy' : 'MMM d, yyyy';
      return `${format(customDateRange.start, fmt, { locale })} - ${format(
        customDateRange.end,
        fmt,
        { locale }
      )}`;
    } else if (dateRange === 'this-month') {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const fmt = isFrench ? 'd MMM, yyyy' : 'MMM d, yyyy';
      return `${t('analytics.dateRange.thisMonth')} (${format(startOfMonth, fmt, { locale })} - ${format(now, fmt, { locale })})`;
    } else if (dateRange === 'last-month') {
      const now = new Date();
      const lastMonthYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
      const lastMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
      const startOfLastMonth = new Date(lastMonthYear, lastMonth, 1);
      const endOfLastMonth = new Date(lastMonthYear, lastMonth + 1, 0);
      const fmt = isFrench ? 'd MMM, yyyy' : 'MMM d, yyyy';
      return `${t('analytics.dateRange.lastMonth')} (${format(startOfLastMonth, fmt, { locale })} - ${format(
        endOfLastMonth,
        fmt,
        { locale }
      )})`;
    } else if (dateRange === 'this-year') {
      const now = new Date();
      return `${t('analytics.dateRange.thisYear', 'Cette année')} (${now.getFullYear()})`;
    } else {
      const num = Number(dateRange);
      const startDate = subDays(baseDate, Number(dateRange));
      const endDate = subDays(baseDate, 1);
      const sameYear = isSameYear(startDate, endDate);
      const fmtNoYear = isFrench ? 'd MMM' : 'MMM d';
      const fmtWithYear = isFrench ? 'd MMM, yyyy' : 'MMM d, yyyy';
      const startDisp = format(startDate, sameYear ? fmtNoYear : fmtWithYear, { locale });
      const endDisp = format(endDate, sameYear ? fmtNoYear : fmtWithYear, { locale });
      if (!Number.isFinite(num) || num <= 0) {
        return `${t('analytics.dateRange.last')} 30 ${t('analytics.dateRange.days')}`;
      }
      if (isFrench) {
        const lastFr = t('analytics.dateRange.lastPlural', 'derniers');
        const daysFr = t('analytics.dateRange.days', 'jours');
        return `${num} ${lastFr} ${daysFr} (${startDisp} - ${endDisp})`;
      }
      return `${t('analytics.dateRange.last')} ${num} ${t('analytics.dateRange.days')} (${startDisp} - ${endDisp})`;
    }
  })();

  return (
    <div className={`relative ${fullWidth ? 'w-full' : ''}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`${fullWidth ? 'w-full justify-start text-left' : ''} inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:text-primary-600 focus:outline-none`}
      >
        <Calendar className="w-4 h-4 mr-2 text-primary-500" />
        {displayedRange}
        <ChevronDown className="w-4 h-4 ml-2" />
      </button>

      {isOpen && (
        <div className={`absolute ${fullWidth ? 'left-0 w-full' : 'right-0 w-80'} mt-2 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-10`}>
          <div className="p-2">
            {dateRangeOptions.map(option => (
              <button
                key={option.value}
                onClick={() => {
                  if (option.value === 'custom') {
                    // Switching to custom should keep the dropdown open and immediately show the date inputs
                    setTempCustomStart(customDateRange.start);
                    setTempCustomEnd(customDateRange.end);
                    onDateRangeChange('custom');
                    // Do NOT close; keep isOpen = true so the panel is visible right away
                    return;
                  } else if (option.value === 'this-month' || option.value === 'last-month' || option.value === 'this-year') {
                    onDateRangeChange(option.value);
                  } else {
                    onDateRangeChange(option.value as number);
                    onCustomDateRangeChange({
                      start: startOfDay(subDays(baseDate, Number(option.value))),
                      // Exclude today: end at yesterday 23:59:59
                      end: endOfDay(subDays(baseDate, 1))
                    });
                  }
                  setIsOpen(false);
                }}
                className={`w-full text-left px-4 py-2 text-sm rounded-lg ${
                  dateRange === option.value
                    ? 'bg-primary-50 text-primary-600'
                    : 'text-gray-700 hover:text-primary-600'
                }`}
              >
                {option.value === 'this-year'
                  ? `${t('analytics.dateRange.thisYear', 'Cette année')} (${new Date().getFullYear()})`
                  : t(`analytics.dateRange.${option.key}`)}
              </button>
            ))}
          </div>

          {dateRange === 'custom' && (
            <div className="p-4 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('analytics.dateRange.startDate')}
                  </label>
                  <DatePicker
                    selected={isNaN(tempCustomStart.getTime()) ? null : tempCustomStart}
                    onChange={(date) => {
                      if (!date || isNaN(date.getTime())) return;
                      setTempCustomStart(clampToWindow(startOfDay(date)));
                    }}
                    minDate={customMinDate}
                    maxDate={customMaxDate}
                    locale={isFrench ? 'fr' : isSpanish ? 'es' : undefined}
                    dateFormat={isFrench ? 'dd/MM/yyyy' : 'yyyy-MM-dd'}
                    showMonthYearDropdown={false}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                    wrapperClassName="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('analytics.dateRange.endDate')}
                  </label>
                  <DatePicker
                    selected={isNaN(tempCustomEnd.getTime()) ? null : tempCustomEnd}
                    onChange={(date) => {
                      if (!date || isNaN(date.getTime())) return;
                      setTempCustomEnd(clampToWindow(endOfDay(date)));
                    }}
                    minDate={customMinDate}
                    maxDate={customMaxDate}
                    locale={isFrench ? 'fr' : isSpanish ? 'es' : undefined}
                    dateFormat={isFrench ? 'dd/MM/yyyy' : 'yyyy-MM-dd'}
                    showMonthYearDropdown={false}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                    wrapperClassName="w-full"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end space-x-2">
                <button
                  onClick={() => setIsOpen(false)}
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 rounded-lg"
                >
                  {t('analytics.dateRange.cancel')}
                </button>
                <button
                  onClick={() => {
                    // Apply the temporary custom dates, clamped to the 12-month window.
                    const safeStart = clampToWindow(tempCustomStart);
                    const safeEnd = clampToWindow(tempCustomEnd);
                    onCustomDateRangeChange({ start: safeStart, end: safeEnd });
                    onDateRangeChange('custom'); // Ensure the mode is set to custom
                    setIsOpen(false);
                  }}
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg"
                >
                  {t('analytics.dateRange.apply')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
